import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthPage } from '@pages/auth';
import { VerifyEmailPage } from '@pages/verify-email';
import { ResetPasswordPage } from '@pages/reset-password';
import { BalancePage } from '@pages/balance';
import { ProjectsPage } from '@pages/projects';
import { ProjectsAddPage } from '@pages/projects-add';
import { ReferralsPage } from '@pages/referrals';
import { WithdrawalsPage } from '@pages/withdrawals';
import { PaymentsPage } from '@pages/payments';
import { ServiceAccountingPage } from '@pages/service-accounting';
import { WalletsPage } from '@pages/wallets';
import { AuditLogPage } from '@pages/audit-log';
import { WithdrawalControlPage } from '@pages/withdrawal-control';
import { CompliancePage } from '@pages/compliance';
import { AmlSettingsPage } from '@pages/aml-settings';
import { AmlBillingPage } from '@pages/aml-billing';
import { SecurityPage } from '@pages/security';
import { WorkspacePage } from '@pages/workspace';
import { ReconciliationPage } from '@pages/reconciliation';
import { InvoicePayPage } from '@pages/invoice-pay';
import { NotFoundPage } from '@pages/not-found';
import { AppShell } from '@widgets/app-shell';
import { useAuth } from '@entities/auth';
import { hasPermission, type Permission } from '@entities/access';
import { buildAuthReturnPath } from '@shared/lib/auth-return-path';
import { AuthBootstrapSkeleton } from '@shared/ui';

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AuthBootstrapSkeleton />;
  }

  return isAuthenticated ? (
    children
  ) : (
    <Navigate
      to="/auth"
      replace
      state={{ from: buildAuthReturnPath(location.pathname, location.search) }}
    />
  );
}

function PermissionRoute({ permission, children }: { permission: Permission; children: React.ReactElement }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AuthBootstrapSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (!hasPermission(user, permission)) {
    return <Navigate to="/balance" replace />;
  }

  return children;
}

function HomeRedirect() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthBootstrapSkeleton />;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  const admin = user?.isAdmin === true || user?.role === 'ADMIN';
  return <Navigate to={admin ? '/workspace' : '/balance'} replace />;
}

function Router(): React.ReactElement {
  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/register" element={<Navigate to="/auth" replace />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="/invoice/:id" element={<InvoicePayPage />} />

      <Route path="/" element={<HomeRedirect />} />
      <Route path="/dashboard" element={<Navigate to="/balance" replace />} />
      <Route
        path="/workspace"
        element={
          <ProtectedRoute>
            <AppShell>
              <WorkspacePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/balance"
        element={
          <ProtectedRoute>
            <AppShell>
              <BalancePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route path="/payments/add" element={<Navigate to="/payments" replace />} />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <AppShell>
              <PaymentsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/add"
        element={
          <ProtectedRoute>
            <AppShell>
              <ProjectsAddPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <AppShell>
              <ProjectsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/withdrawals"
        element={
          <ProtectedRoute>
            <AppShell>
              <WithdrawalsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <AppShell>
              <SecurityPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReferralsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-accounting"
        element={
          <PermissionRoute permission="accounting.read_any">
            <AppShell>
              <ServiceAccountingPage />
            </AppShell>
          </PermissionRoute>
        }
      />
      <Route
        path="/wallets"
        element={
          <PermissionRoute permission="wallet.read">
            <AppShell>
              <WalletsPage />
            </AppShell>
          </PermissionRoute>
        }
      />
      <Route
        path="/withdrawal-control"
        element={
          <PermissionRoute permission="platform.manage">
            <AppShell>
              <WithdrawalControlPage />
            </AppShell>
          </PermissionRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <PermissionRoute permission="audit.read">
            <AppShell>
              <AuditLogPage />
            </AppShell>
          </PermissionRoute>
        }
      />
      <Route
        path="/reconciliation"
        element={
          <PermissionRoute permission="reconciliation.read">
            <AppShell>
              <ReconciliationPage />
            </AppShell>
          </PermissionRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <AppShell>
              <CompliancePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/aml-settings"
        element={
          <ProtectedRoute>
            <AppShell>
              <AmlSettingsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/aml-billing"
        element={
          <PermissionRoute permission="aml.billing.manage">
            <AppShell>
              <AmlBillingPage />
            </AppShell>
          </PermissionRoute>
        }
      />
      <Route path="/payment-solutions" element={<Navigate to="/balance" replace />} />

      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}

export default Router;
